import React, { useCallback, useMemo } from 'react';
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, Node, Edge, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { UserIcon, UsersIcon, DollarSignIcon } from 'lucide-react';
import { Employee, ReferralNetwork } from '../data/mockReferralData';
import { buildReferralTree, treeToFlowElements, calculateIndirectReferrals } from '../utils/referralCalculations';
// Format funding amount helper
function formatFunding(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2)}M`;
  }
  return `$${amount}K`;
}
type ReferralNodeData = {
  employee: Employee;
  depth: number;
  isRoot: boolean;
};
type ReferralNodeProps = {
  data: ReferralNodeData;
};
function ReferralNode({
  data
}: ReferralNodeProps) {
  const {
    employee,
    depth,
    isRoot
  } = data;
  const depthColors = [{
    bg: 'bg-blue-600',
    border: 'border-blue-700',
    text: 'text-white'
  }, {
    bg: 'bg-blue-100',
    border: 'border-blue-300',
    text: 'text-blue-900'
  }, {
    bg: 'bg-purple-100',
    border: 'border-purple-300',
    text: 'text-purple-900'
  }, {
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    text: 'text-gray-900'
  }];
  const colorIndex = Math.min(depth, depthColors.length - 1);
  const colors = isRoot ? depthColors[0] : depthColors[colorIndex];
  return <div className={`
        px-4 py-3 rounded-lg shadow-md border-2 min-w-[220px]
        ${colors.bg} ${colors.border} ${colors.text}
        ${isRoot ? 'ring-4 ring-blue-300 ring-opacity-50' : ''}
      `} data-id="element-109">
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-3 !h-3" data-id="element-110" />

      <div className="flex items-start gap-3" data-id="element-111">
        <div className={`
          p-2 rounded-full
          ${isRoot ? 'bg-blue-500' : 'bg-white/50'}
        `} data-id="element-112">
          <UserIcon className={`w-5 h-5 ${isRoot ? 'text-white' : 'text-gray-600'}`} data-id="element-113" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-114">
          <p className={`font-semibold text-sm truncate ${colors.text}`} data-id="element-115">
            {employee.name}
          </p>
          <p className={`text-xs truncate ${isRoot ? 'text-blue-100' : 'text-gray-500'}`} data-id="element-116">
            {employee.title}
          </p>
          <p className={`text-xs truncate ${isRoot ? 'text-blue-200' : 'text-gray-400'}`} data-id="element-117">
            {employee.department}
          </p>
        </div>
      </div>

      {/* Funding Amount */}
      <div className={`mt-2 pt-2 border-t ${isRoot ? 'border-blue-500' : 'border-gray-300'}`} data-id="element-118">
        <div className="flex items-center gap-2" data-id="element-119">
          <DollarSignIcon className={`w-3 h-3 ${isRoot ? 'text-blue-200' : 'text-gray-500'}`} data-id="element-120" />
          <span className={`text-xs font-semibold ${isRoot ? 'text-blue-100' : 'text-gray-600'}`} data-id="element-121">
            {formatFunding(employee.fundingAmount)} funded
          </span>
        </div>
      </div>

      {isRoot && <div className="mt-1" data-id="element-122">
          <span className="text-xs text-blue-100 font-medium" data-id="element-123">
            Root Referrer
          </span>
        </div>}

      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !w-3 !h-3" data-id="element-124" />
    </div>;
}
const nodeTypes = {
  referralNode: ReferralNode
};
type ReferralTreeViewProps = {
  employeeId: string;
  network: ReferralNetwork;
  inline?: boolean;
};
export function ReferralTreeView({
  employeeId,
  network,
  inline = false
}: ReferralTreeViewProps) {
  const employee = network.employees[employeeId];
  const directReferrals = network.referrals[employeeId] || [];
  const indirectCount = calculateIndirectReferrals(employeeId, network.referrals);
  const {
    initialNodes,
    initialEdges
  } = useMemo(() => {
    const tree = buildReferralTree(employeeId, network);
    if (!tree) {
      return {
        initialNodes: [],
        initialEdges: []
      };
    }
    const {
      nodes,
      edges
    } = treeToFlowElements(tree);
    return {
      initialNodes: nodes,
      initialEdges: edges
    };
  }, [employeeId, network]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges as Edge[]);
  const onInit = useCallback((reactFlowInstance: any) => {
    reactFlowInstance.fitView({
      padding: 0.2
    });
  }, []);
  // Inline compact version
  if (inline) {
    return <div className="bg-white rounded-lg border border-gray-200" data-id="element-125">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200" data-id="element-126">
          <div className="flex items-center justify-between" data-id="element-127">
            <div data-id="element-128">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-129">
                {employee?.name}'s Referral Network
              </h3>
              <p className="text-sm text-gray-600" data-id="element-130">
                {employee?.title} • {employee?.department}
              </p>
            </div>

            {/* Compact Stats */}
            <div className="flex items-center gap-4" data-id="element-131">
              <div className="text-center" data-id="element-132">
                <p className="text-2xl font-bold text-blue-600" data-id="element-133">
                  {directReferrals.length}
                </p>
                <p className="text-xs text-gray-500" data-id="element-134">Direct</p>
              </div>
              <div className="text-center" data-id="element-135">
                <p className="text-2xl font-bold text-purple-600" data-id="element-136">
                  {indirectCount}
                </p>
                <p className="text-xs text-gray-500" data-id="element-137">Indirect</p>
              </div>
              <div className="text-center" data-id="element-138">
                <p className="text-2xl font-bold text-green-600" data-id="element-139">
                  {directReferrals.length + indirectCount}
                </p>
                <p className="text-xs text-gray-500" data-id="element-140">Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Tree */}
        <div className="h-[500px] bg-white" data-id="element-141">
          {nodes.length === 0 ? <div className="flex items-center justify-center h-full" data-id="element-142">
              <div className="text-center" data-id="element-143">
                <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-144" />
                <p className="text-gray-500" data-id="element-145">No referrals to display</p>
              </div>
            </div> : <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onInit={onInit} nodeTypes={nodeTypes} fitView attributionPosition="bottom-right" minZoom={0.3} maxZoom={1.2} data-id="element-146">
              <Background color="#e5e7eb" gap={16} size={1} data-id="element-147" />
              <Controls showInteractive={false} data-id="element-148" />
              <MiniMap nodeColor={node => {
            const data = node.data as ReferralNodeData;
            if (data.isRoot) return '#2563eb';
            if (data.depth === 1) return '#93c5fd';
            if (data.depth === 2) return '#c4b5fd';
            return '#d1d5db';
          }} maskColor="rgba(0, 0, 0, 0.05)" style={{
            height: 80,
            width: 120
          }} data-id="element-149" />
            </ReactFlow>}
        </div>

        {/* Compact Legend */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200" data-id="element-150">
          <div className="flex items-center gap-6 text-xs text-gray-600" data-id="element-151">
            <div className="flex items-center gap-2" data-id="element-152">
              <span className="w-3 h-3 rounded bg-blue-600" data-id="element-153"></span>
              <span data-id="element-154">Root</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-155">
              <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300" data-id="element-156"></span>
              <span data-id="element-157">Direct (Level 1)</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-158">
              <span className="w-3 h-3 rounded bg-purple-100 border border-purple-300" data-id="element-159"></span>
              <span data-id="element-160">Indirect (Level 2+)</span>
            </div>
          </div>
        </div>
      </div>;
  }
  // Full page version (kept for potential future use)
  return <div className="flex flex-col h-screen w-full" data-id="element-161">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0" data-id="element-162">
        <div className="flex items-center justify-between" data-id="element-163">
          <div className="flex items-center gap-4" data-id="element-164">
            <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Back to referral table" data-id="element-165">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-166" />
              <span className="font-medium" data-id="element-167">Back to Table</span>
            </button>

            <div className="h-8 w-px bg-gray-200" data-id="element-168"></div>

            <div data-id="element-169">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-170">
                {employee?.name}'s Referral Network
              </h2>
              <p className="text-sm text-gray-500" data-id="element-171">
                {employee?.title} • {employee?.department}
              </p>
            </div>
          </div>

          {/* Stats summary */}
          <div className="flex items-center gap-6" data-id="element-172">
            <div className="flex items-center gap-2" data-id="element-173">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-174">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-175" />
              </div>
              <div data-id="element-176">
                <p className="text-2xl font-bold text-blue-600" data-id="element-177">
                  {directReferrals.length}
                </p>
                <p className="text-xs text-gray-500" data-id="element-178">Direct</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-179">
              <div className="p-2 bg-purple-100 rounded-lg" data-id="element-180">
                <UsersIcon className="w-5 h-5 text-purple-600" data-id="element-181" />
              </div>
              <div data-id="element-182">
                <p className="text-2xl font-bold text-purple-600" data-id="element-183">
                  {indirectCount}
                </p>
                <p className="text-xs text-gray-500" data-id="element-184">Indirect</p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-185">
              <div className="p-2 bg-green-100 rounded-lg" data-id="element-186">
                <UsersIcon className="w-5 h-5 text-green-600" data-id="element-187" />
              </div>
              <div data-id="element-188">
                <p className="text-2xl font-bold text-green-600" data-id="element-189">
                  {directReferrals.length + indirectCount}
                </p>
                <p className="text-xs text-gray-500" data-id="element-190">Total</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tree visualization */}
      <div className="flex-1 bg-gray-50 w-full overflow-hidden" data-id="element-191">
        {nodes.length === 0 ? <div className="flex items-center justify-center h-full" data-id="element-192">
            <div className="text-center" data-id="element-193">
              <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-194" />
              <p className="text-lg text-gray-500" data-id="element-195">No referrals to display</p>
              <p className="text-sm text-gray-400" data-id="element-196">
                This employee hasn't made any referrals yet
              </p>
            </div>
          </div> : <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onInit={onInit} nodeTypes={nodeTypes} fitView attributionPosition="bottom-left" minZoom={0.2} maxZoom={1.5} data-id="element-197">
            <Background color="#e5e7eb" gap={20} data-id="element-198" />
            <Controls data-id="element-199" />
            <MiniMap nodeColor={node => {
          const data = node.data as ReferralNodeData;
          if (data.isRoot) return '#2563eb';
          if (data.depth === 1) return '#93c5fd';
          if (data.depth === 2) return '#c4b5fd';
          return '#d1d5db';
        }} maskColor="rgba(0, 0, 0, 0.1)" data-id="element-200" />
          </ReactFlow>}
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-gray-200 px-6 py-3 flex-shrink-0" data-id="element-201">
        <div className="flex flex-wrap items-center gap-6 text-sm" data-id="element-202">
          <span className="text-gray-500 font-medium" data-id="element-203">Legend:</span>
          <div className="flex items-center gap-2" data-id="element-204">
            <span className="w-4 h-4 rounded bg-blue-600 border border-blue-700" data-id="element-205"></span>
            <span className="text-gray-600" data-id="element-206">Root (Selected Employee)</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-207">
            <span className="w-4 h-4 rounded bg-blue-100 border border-blue-300" data-id="element-208"></span>
            <span className="text-gray-600" data-id="element-209">Direct Referrals (Level 1)</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-210">
            <span className="w-4 h-4 rounded bg-purple-100 border border-purple-300" data-id="element-211"></span>
            <span className="text-gray-600" data-id="element-212">Indirect Referrals (Level 2+)</span>
          </div>
        </div>
      </div>
    </div>;
}